import React, { useState } from 'react';
import { Paper, TextField, Grid, Typography, Button, Box, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const ColorsTab = () => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const handleSaveColors = async () => {
    try {
      setSaving(true);
      
      // Preparar updates para todos los campos de colores que sean configurables
      const updates = {};
      
      // Colores
      Object.keys(config.theme.colors || {}).forEach(colorKey => {
        const fieldPath = `theme.colors.${colorKey}`;
        if (isFieldConfigurable(fieldPath)) {
          updates[fieldPath] = config.theme.colors[colorKey];
        }
      });

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      
      // Recargar configuración
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving colors:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  // Filtrar solo colores configurables
  const editableColors = Object.keys(config.theme.colors || {}).filter(colorKey => 
    isFieldConfigurable(`theme.colors.${colorKey}`)
  );
  
  const hasEditableColors = editableColors.length > 0;

  if (!hasEditableColors) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('colors') || 'Colores'}</Typography>
        </Grid>
        {editableColors.map((colorKey) => {
          const fieldPath = `theme.colors.${colorKey}`;
          
          return (
            <Grid item xs={12} md={6} key={colorKey}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: config.theme.colors[colorKey],
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <TextField
                    fullWidth
                    label={t(colorKey) || colorKey}
                    value={config.theme.colors[colorKey] || ''}
                    onChange={(e) => handleChange(fieldPath, e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSaveColors} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save_colors') || 'Guardar Colores')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ColorsTab;

